import React, { useState, useEffect } from 'react';
import { FiDownload, FiClock, FiUser, FiCalendar } from 'react-icons/fi';
import api from '../../services/api';

const TimesheetManagementPage = () => {
  const [timesheets, setTimesheets] = useState({});
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [editingEntry, setEditingEntry] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [projectSearch, setProjectSearch] = useState('');
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);

  useEffect(() => {
    fetchTimesheets();
    fetchProjects();
  }, []);

  const fetchTimesheets = async () => {
    try {
      const response = await api.get('/timesheets/admin/all');
      console.log('Fetched timesheets:', response.data);
      setTimesheets(response.data);
    } catch (error) {
      console.error('Error fetching timesheets:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async (search = '') => {
    try {
      const response = await api.post('/projects/list', { search: search || undefined, limit: 100, page: 1 });
      setProjects(response.data.projects || response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const downloadProjectTimesheet = (projectName, entries) => {
    const csvContent = [
      ['Date', 'User Name', 'User Email', 'Task Name', 'Task Description', 'Status', 'Hours', 'Entry Time'],
      ...entries.map(entry => [
        new Date(entry.date).toLocaleDateString(),
        entry.User?.name || 'Unknown',
        entry.User?.email || 'Unknown',
        entry.Task ? entry.Task.title : entry.task_name,
        entry.Task?.description || entry.task_description || 'No description',
        entry.status,
        entry.hours,
        new Date(entry.created_at).toLocaleString()
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${projectName}_timesheet.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const downloadAllTimesheets = () => {
    const allEntries = Object.values(timesheets).flatMap(project => project.entries);
    const csvContent = [
      ['Date', 'Project', 'User Name', 'User Email', 'Task Name', 'Task Description', 'Status', 'Hours', 'Entry Time'],
      ...allEntries.map(entry => [
        new Date(entry.date).toLocaleDateString(),
        entry.Project?.name || 'No Project',
        entry.User?.name || 'Unknown',
        entry.User?.email || 'Unknown',
        entry.Task ? entry.Task.title : entry.task_name,
        entry.Task?.description || entry.task_description || 'No description',
        entry.status,
        entry.hours,
        new Date(entry.created_at).toLocaleString()
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'all_timesheets.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getFilteredData = () => {
    let filteredTimesheets = { ...timesheets };
    
    // Filter by project
    if (selectedProject !== 'all') {
      const project = projects.find(p => p.id === parseInt(selectedProject));
      if (project && timesheets[project.name]) {
        filteredTimesheets = { [project.name]: timesheets[project.name] };
      } else {
        filteredTimesheets = {};
      }
    }
    
    // Filter by date range
    if (startDate || endDate) {
      const filtered = {};
      Object.entries(filteredTimesheets).forEach(([projectName, data]) => {
        const filteredEntries = data.entries.filter(entry => {
          const entryDate = new Date(entry.date);
          const start = startDate ? new Date(startDate) : null;
          const end = endDate ? new Date(endDate) : null;
          
          if (start && entryDate < start) return false;
          if (end && entryDate > end) return false;
          return true;
        });
        
        if (filteredEntries.length > 0) {
          filtered[projectName] = {
            ...data,
            entries: filteredEntries,
            totalHours: filteredEntries.reduce((sum, entry) => sum + parseFloat(entry.hours), 0)
          };
        }
      });
      filteredTimesheets = filtered;
    }
    
    return filteredTimesheets;
  };

  const downloadSelectedProjectTimesheet = () => {
    const filteredData = getFilteredData();
    
    if (selectedProject === 'all') {
      // Download all filtered data
      const allEntries = Object.values(filteredData).flatMap(project => project.entries);
      if (allEntries.length === 0) {
        alert('No timesheet data found for selected filters');
        return;
      }
      
      const csvContent = [
        ['Date', 'Project', 'User Name', 'User Email', 'Task Name', 'Task Description', 'Status', 'Hours', 'Entry Time'],
        ...allEntries.map(entry => [
          new Date(entry.date).toLocaleDateString(),
          entry.Project?.name || 'No Project',
          entry.User?.name || 'Unknown',
          entry.User?.email || 'Unknown',
          entry.Task ? entry.Task.title : entry.task_name,
          entry.Task?.description || entry.task_description || 'No description',
          entry.status,
          entry.hours,
          new Date(entry.created_at).toLocaleString()
        ])
      ].map(row => row.join(',')).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `filtered_timesheets_${startDate || 'all'}_to_${endDate || 'all'}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } else {
      // Download specific project filtered data
      const project = projects.find(p => p.id === parseInt(selectedProject));
      if (project && filteredData[project.name]) {
        downloadProjectTimesheet(`${project.name}_filtered`, filteredData[project.name].entries);
      } else {
        alert('No timesheet data found for selected filters');
      }
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry.id);
    setEditForm({
      hours: entry.hours,
      status: entry.status,
      date: entry.date.split('T')[0]
    });
  };

  const handleSave = async (entryId) => {
    try {
      await api.put(`/timesheets/admin/${entryId}`, editForm);
      setEditingEntry(null);
      fetchTimesheets();
      alert('Timesheet updated successfully!');
    } catch (error) {
      console.error('Error updating timesheet:', error);
      alert('Failed to update timesheet');
    }
  };

  const handleCancel = () => {
    setEditingEntry(null);
    setEditForm({});
  };

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold mb-6">Timesheet Management</h1>
        <div className="text-center py-8">Loading timesheets...</div>
      </div>
    );
  }

  const filteredData = getFilteredData();
  const totalEntries = Object.values(timesheets).reduce((sum, project) => sum + project.entries.length, 0);
  const totalHours = Object.values(timesheets).reduce((sum, project) => sum + project.totalHours, 0);

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Timesheet Management</h1>
        {Object.keys(filteredData).length > 0 && (
          <button
            onClick={downloadSelectedProjectTimesheet}
            className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 flex items-center"
          >
            <FiDownload className="mr-2" /> 
            {selectedProject === 'all' ? 'Download All' : 'Download Selected'}
          </button>
        )}
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <FiClock className="text-blue-500 mr-3" size={24} />
            <div>
              <h3 className="text-sm font-medium text-gray-500">Total Hours</h3>
              <p className="text-2xl font-semibold text-gray-900">{totalHours.toFixed(2)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <FiCalendar className="text-green-500 mr-3" size={24} />
            <div>
              <h3 className="text-sm font-medium text-gray-500">Total Entries</h3>
              <p className="text-2xl font-semibold text-gray-900">{totalEntries}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <FiUser className="text-purple-500 mr-3" size={24} />
            <div>
              <h3 className="text-sm font-medium text-gray-500">Projects</h3>
              <p className="text-2xl font-semibold text-gray-900">{Object.keys(timesheets).length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">Project:</label>
            <input
              type="text"
              value={projectSearch}
              onChange={(e) => {
                setProjectSearch(e.target.value);
                setProjectDropdownOpen(true);
                fetchProjects(e.target.value);
              }}
              onFocus={() => setProjectDropdownOpen(true)}
              onBlur={() => setTimeout(() => setProjectDropdownOpen(false), 200)}
              placeholder="Search project..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
            {projectDropdownOpen && (
              <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto mt-1">
                <button
                  type="button"
                  onMouseDown={() => { setSelectedProject('all'); setProjectSearch(''); setProjectDropdownOpen(false); }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${selectedProject === 'all' ? 'bg-blue-50 text-blue-700' : ''}`}
                >
                  All Projects
                </button>
                {projects.map(project => (
                  <button
                    key={project.id}
                    type="button"
                    onMouseDown={() => { setSelectedProject(String(project.id)); setProjectSearch(project.name); setProjectDropdownOpen(false); }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${selectedProject === String(project.id) ? 'bg-blue-50 text-blue-700' : ''}`}
                  >
                    {project.name} {timesheets[project.name] ? `(${timesheets[project.name].entries.length})` : ''}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date:</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date:</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>
          
          <div>
            <button
              onClick={() => {
                setSelectedProject('all');
                setStartDate('');
                setEndDate('');
              }}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Timesheet Data */}
      {Object.keys(filteredData).length === 0 ? (
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <p className="text-gray-500">No timesheet entries found.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(filteredData).map(([projectName, data]) => (
            <div key={projectName} className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-lg font-medium text-gray-900">{projectName}</h2>
                    <p className="text-sm text-gray-500">
                      {data.entries.length} entries • {data.totalHours.toFixed(2)} total hours
                    </p>
                  </div>
                  <button
                    onClick={() => downloadProjectTimesheet(projectName, data.entries)}
                    className="px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center"
                  >
                    <FiDownload className="mr-1" size={14} />
                    Download
                  </button>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Task</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hours</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entry Time</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {data.entries.map(entry => (
                      <tr key={entry.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {editingEntry === entry.id ? (
                            <input
                              type="date"
                              value={editForm.date}
                              onChange={(e) => setEditForm({...editForm, date: e.target.value})}
                              className="text-sm border rounded px-2 py-1"
                            />
                          ) : (
                            new Date(entry.date).toLocaleDateString()
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {entry.User?.name || 'Unknown'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {entry.User?.email || 'Unknown'}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div className="font-medium">
                            {entry.Task ? entry.Task.title : entry.task_name}
                          </div>
                          <div className="text-xs text-gray-500 mt-1 max-w-xs">
                            {entry.Task?.description || 'No description'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingEntry === entry.id ? (
                            <select
                              value={editForm.status}
                              onChange={(e) => setEditForm({...editForm, status: e.target.value})}
                              className="text-sm border rounded px-2 py-1"
                            >
                              <option value="in_progress">In Progress</option>
                              <option value="completed">Completed</option>
                              <option value="on_hold">On Hold</option>
                            </select>
                          ) : (
                            <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                              {entry.status.replace('_', ' ')}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {editingEntry === entry.id ? (
                            <input
                              type="number"
                              step="0.5"
                              value={editForm.hours}
                              onChange={(e) => setEditForm({...editForm, hours: e.target.value})}
                              className="text-sm border rounded px-2 py-1 w-20"
                            />
                          ) : (
                            `${entry.hours} hrs`
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {editingEntry === entry.id ? (
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleSave(entry.id)}
                                className="text-green-600 hover:text-green-800 text-xs"
                              >
                                Save
                              </button>
                              <button
                                onClick={handleCancel}
                                className="text-red-600 hover:text-red-800 text-xs"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <span>{new Date(entry.created_at).toLocaleString()}</span>
                              <button
                                onClick={() => handleEdit(entry)}
                                className="text-blue-600 hover:text-blue-800 text-xs"
                              >
                                Edit
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TimesheetManagementPage;